import React, { useContext, useEffect } from "react";
import Button from "@mui/material/Button";
import { useTranslation } from "react-i18next";

import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import useAccountSettings from "../../ManageAccount/useAccountSettings";
import AppContext from "../../appContext";
import useSaveSurvey from "./useSaveSurvey";
import { makeItemId } from "../../tools";
import MutationMessage from "../../Components/MutationMessage";

export default function SaveButton({
  creator,
  interactionId,
  onClick: checkForErrors,
  questionList,
  setCreator,
  setDetails,
  isBlocking,
  onSuccess,
  isError,
  disabled = false,
}) {
  const { t } = useTranslation();
  const {
    app: {
      client: { writeAccess },
    },
  } = useContext(AppContext);
  const { accountSettings } = useAccountSettings();
  const { exportPublicMutation, saveLocalMutation, savePublicMutation } =
    useSaveSurvey();

  useEffect(() => {
    if (interactionId === -1) setCreator(accountSettings);
  }, [accountSettings]);

  const handleSave = (type) => {
    checkForErrors();
    if (isError) {
      return;
    }
    if (type === "saveLocal") {
      saveLocalMutation.mutate({ questionList, interactionId, onSuccess });
    }
    if (type === "savePublic") {
      savePublicMutation.mutate({ questionList, interactionId, onSuccess });
    }
    if (type === "exportPublic") {
      const itemId = makeItemId(); // generate a new itemId on first export
      exportPublicMutation.mutate({
        questionList: {
          ...questionList,
          itemId,
        },
        onSuccess: (data) => {
          onSuccess(data);
          setCreator(accountSettings); // I'm the owner now
          setDetails((prev) => ({ ...prev, itemId }));
        },
      });
    }
  };

  // show export button for public interaction that are not mine OR local interactions of shared clients
  if (
    (typeof interactionId === "number" &&
      accountSettings.code !== creator.code) ||
    (typeof interactionId === "string" && !writeAccess)
  ) {
    return (
      <div>
        <MutationMessage
          mutation={exportPublicMutation}
          text={{
            loading: t("common.notifications.exportLoading"),
            success: t("common.notifications.exportSuccess"),
          }}
        />
        <Button
          aria-label="export public survey"
          variant="outlined"
          size="medium"
          onClick={() => handleSave("exportPublic")}
          startIcon={<SaveOutlinedIcon />}
        >
          {t("surveys.export")}
        </Button>
      </div>
    );
  }
  // show button "save in my libary" for public interactions that I created
  if (
    typeof interactionId === "number" &&
    accountSettings.code === creator.code
  ) {
    return (
      <div>
        <MutationMessage
          mutation={savePublicMutation}
          text={{
            loading: t("common.notifications.saveLoading"),
            success: t("common.notifications.saveSuccess"),
          }}
        />
        <Button
          aria-label="save public survey"
          variant="outlined"
          size="medium"
          onClick={() => handleSave("savePublic")}
          startIcon={<SaveOutlinedIcon />}
        >
          {t("editor.buttons.saveInLibrary")}
        </Button>
      </div>
    );
  }
  // show save button for local interactions that I created for my client
  if (typeof interactionId === "string" && writeAccess)
    return (
      <div>
        <MutationMessage
          mutation={saveLocalMutation}
          text={{
            loading: t("common.notifications.saveLoading"),
            success: t("common.notifications.saveSuccess"),
          }}
        />
        <Button
          aria-label="save local survey"
          variant="outlined"
          size="medium"
          onClick={() => handleSave("saveLocal")}
          startIcon={<SaveOutlinedIcon />}
          disabled={disabled || !isBlocking}
        >
          {t("common.buttons.save")}
        </Button>
      </div>
    );
  // otherwize: show nothing
  return "";
}
